/**
 * Credential config form generated from `providers.config_schema` (D-02).
 * Fields marked `x-secret` render as passwords and are never prefilled from the server.
 */
import { useTranslation } from "react-i18next";
import { FieldStack, SelectInput, SwitchInput, TextAreaInput, TextInput } from "./fields";

export interface JsonSchemaProperty {
  type?: string | string[];
  title?: string;
  description?: string;
  default?: unknown;
  enum?: unknown[];
  format?: string;
  minimum?: number;
  maximum?: number;
  example?: unknown;
  "x-secret"?: boolean;
  "x-widget"?: string;
}

export interface JsonObjectSchema {
  properties?: Record<string, JsonSchemaProperty>;
  required?: string[];
  /** Field order from the adapter schema. Postgres jsonb does not keep key order. */
  "x-order"?: string[];
}

export type FormValues = Record<string, string | boolean>;

function baseType(p: JsonSchemaProperty): string {
  const t = Array.isArray(p.type) ? p.type.find((x) => x !== "null") : p.type;
  return t ?? "string";
}

function humanize(name: string) {
  return name.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

/** Schema properties in their original order. */
export function orderedProperties(schema: JsonObjectSchema): Array<[string, JsonSchemaProperty]> {
  const props = schema.properties ?? {};
  const order = schema["x-order"] ?? [];
  const rank = (k: string) => {
    const i = order.indexOf(k);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  return Object.entries(props).sort(([a], [b]) => rank(a) - rank(b));
}

/** Initial form values. Edit mode uses the public config; secret fields always start empty. */
export function initialValues(schema: JsonObjectSchema, current?: Record<string, unknown>): FormValues {
  const out: FormValues = {};
  for (const [name, p] of Object.entries(schema.properties ?? {})) {
    const source = current ? current[name] : p.default;
    if (baseType(p) === "boolean") out[name] = Boolean(source ?? p.default ?? false);
    else if (p["x-secret"]) out[name] = "";
    else out[name] = source === undefined || source === null ? "" : String(source);
  }
  return out;
}

/** Turn form values into an API config. Create mode drops empty fields; edit mode sends them to clear. */
export function toConfig(schema: JsonObjectSchema, values: FormValues, mode: "create" | "edit") {
  const config: Record<string, unknown> = {};
  for (const [name, p] of Object.entries(schema.properties ?? {})) {
    const v = values[name];
    const type = baseType(p);
    if (type === "boolean") {
      config[name] = Boolean(v);
      continue;
    }
    const s = typeof v === "string" ? v.trim() : "";
    if (s === "") {
      if (mode === "edit") config[name] = "";
      continue;
    }
    config[name] = type === "integer" || type === "number" ? Number(s) : s;
  }
  return config;
}

export function SchemaForm({
  schema,
  values,
  onChange,
  secretFieldsSet = [],
  errors = {},
}: {
  schema: JsonObjectSchema;
  values: FormValues;
  onChange: (values: FormValues) => void;
  secretFieldsSet?: string[];
  errors?: Record<string, string>;
}) {
  const { t } = useTranslation();
  const required = new Set(schema.required ?? []);
  const set = (name: string, value: string | boolean) => onChange({ ...values, [name]: value });

  return (
    <FieldStack>
      {orderedProperties(schema).map(([name, p]) => {
        const label = p.title ?? humanize(name);
        const type = baseType(p);
        const error = errors[name];
        const isSecretSet = Boolean(p["x-secret"] && secretFieldsSet.includes(name));
        const hint = isSecretSet ? t("credentials.secretSet") : p.description;

        if (type === "boolean") {
          return (
            <SwitchInput
              key={name}
              label={label}
              description={hint}
              isSelected={Boolean(values[name])}
              onChange={(v) => set(name, v)}
            />
          );
        }

        if (p.enum) {
          return (
            <SelectInput
              key={name}
              label={label}
              description={hint}
              error={error}
              isRequired={required.has(name)}
              value={(values[name] as string) || null}
              onChange={(v) => set(name, v ?? "")}
              options={p.enum.map((o) => ({ id: String(o), label: String(o) }))}
            />
          );
        }

        if (p["x-widget"] === "textarea") {
          return (
            <TextAreaInput
              key={name}
              name={name}
              label={label}
              description={hint}
              error={error}
              isRequired={required.has(name) && !isSecretSet}
              value={String(values[name] ?? "")}
              onChange={(v) => set(name, v)}
              placeholder={isSecretSet ? "••••••••" : undefined}
              mono
            />
          );
        }

        const inputType = p["x-secret"]
          ? "password"
          : type === "integer" || type === "number"
            ? "number"
            : p.format === "email"
              ? "email"
              : "text";
        const placeholder = isSecretSet
          ? "••••••••"
          : p.example !== undefined
            ? String(p.example)
            : p.default !== undefined
              ? String(p.default)
              : undefined;

        return (
          <TextInput
            key={name}
            name={name}
            type={inputType}
            label={label}
            description={hint}
            error={error}
            isRequired={required.has(name) && !isSecretSet}
            value={String(values[name] ?? "")}
            onChange={(v) => set(name, v)}
            placeholder={placeholder}
            autoComplete={p["x-secret"] ? "new-password" : "off"}
            min={p.minimum}
            max={p.maximum}
          />
        );
      })}
    </FieldStack>
  );
}
